// SharePoint Image Document Library Service - Uses existing SharePoint connection
import { sharepointServiceProduction } from './sharepointServiceProduction'

export interface SharePointImageFile {
  id: string
  name: string
  webUrl: string
  downloadUrl: string
  category: string
  size: number
  created: string
  modified: string
  libraryId?: string     // SharePoint library ID (needed for proxy)
  thumbnailUrl?: string
  localDataUrl?: string  // For cached local image data
  isSelected?: boolean   // For website usage
  itemId?: string        // Associated item ID
  // Gallery-specific fields
  title?: string         // Image title
  description?: string   // Image description
  tags?: string[]        // Tags array
  isActive?: boolean     // Visibility toggle
  eTag?: string          // For change detection
  lastModified?: string  // Last modified timestamp
}

class SharePointImageService {
  private imageLibraryName = 'Website Images'
  private galleryLibraryName = 'SaltyMermaid_Gallery'
  private imageLibraryId: string | null = null
  private galleryLibraryId: string | null = null
  private subscribers: (() => void)[] = []

  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      // Use the existing SharePoint service connection
      const result = await sharepointServiceProduction.testConnection()
      if (result.success) {
        return { success: true, message: 'Connected to SharePoint successfully' }
      }
      return result
    } catch (error: any) {
      console.error('SharePoint connection test failed:', error)
      return { 
        success: false, 
        message: error.message || 'Failed to connect to SharePoint' 
      }
    }
  }

  async createImageLibrary(): Promise<{ success: boolean; message: string }> {
    try {
      // Use the existing service to create the image library
      return await sharepointServiceProduction.createImageLibrary()
    } catch (error: any) {
      console.error('Failed to create image library:', error)
      return { 
        success: false, 
        message: `Failed to create image library: ${error.message}` 
      }
    }
  }

  private async getImageLibraryId(category?: string): Promise<string | null> {
    // Use Art Gallery Images library for gallery category
    const isGallery = category === 'gallery'
    const libraryName = isGallery ? this.galleryLibraryName : this.imageLibraryName
    const cachedId = isGallery ? this.galleryLibraryId : this.imageLibraryId

    console.log(`🔍 Getting library ID for category: ${category}, libraryName: ${libraryName}`)

    if (cachedId) {
      console.log(`✅ Using cached library ID: ${cachedId}`)
      return cachedId
    }

    const graphClient = await sharepointServiceProduction.getGraphClient()
    const siteId = await sharepointServiceProduction.getSiteIdForImages()

    if (!graphClient || !siteId) {
      console.error('❌ SharePoint client or site ID not available')
      return null
    }

    try {
      console.log(`🔍 Looking for library: ${libraryName}`)

      const existingLibrary = await graphClient
        .api(`/sites/${siteId}/lists`)
        .filter(`displayName eq '${libraryName}'`)
        .get()

      console.log(`📚 Found ${existingLibrary.value?.length || 0} libraries matching "${libraryName}"`)

      if (existingLibrary.value && existingLibrary.value.length > 0) {
        console.log('📋 Library details:', JSON.stringify(existingLibrary.value[0], null, 2))
      }

      if (existingLibrary.value && existingLibrary.value.length > 0) {
        const library = existingLibrary.value[0]
        const libraryId = library.id
        console.log(`✅ Found existing library ID: ${libraryId}`)

        // Check if it's a document library (has a drive)
        // Document libraries have list.template === 'documentLibrary' or list object with documentLibrary info
        const isDocumentLibrary = library.list?.template === 'documentLibrary' ||
                                   library.template === 'documentLibrary' ||
                                   library.baseTemplate === 101 // Document library base template

        console.log(`📚 Library type check - isDocumentLibrary: ${isDocumentLibrary}, template: ${library.list?.template || library.template}, baseTemplate: ${library.baseTemplate}`)

        if (!isDocumentLibrary) {
          console.error(`❌ "${libraryName}" exists but is NOT a document library! It's a regular list.`)
          console.error('⚠️ Please either:')
          console.error('   1. Delete the existing "SaltyMermaid_Gallery" list in SharePoint')
          console.error('   2. Or rename it so we can create a proper document library')
          return null
        }

        if (isGallery) {
          this.galleryLibraryId = libraryId
        } else {
          this.imageLibraryId = libraryId
        }
        return libraryId
      }

      // Library doesn't exist, try to create it
      console.log(`⚠️ Library "${libraryName}" not found, attempting to create...`)

      const createResult = isGallery
        ? await this.createGalleryImageLibrary()
        : await this.createImageLibrary()

      console.log('Create library result:', createResult)

      if (createResult.success) {
        // Try to get the ID again
        const newLibrary = await graphClient
          .api(`/sites/${siteId}/lists`)
          .filter(`displayName eq '${libraryName}'`)
          .get()

        if (newLibrary.value && newLibrary.value.length > 0) {
          const libraryId = newLibrary.value[0].id
          console.log(`✅ Created library with ID: ${libraryId}`)

          if (isGallery) {
            this.galleryLibraryId = libraryId
          } else {
            this.imageLibraryId = libraryId
          }
          return libraryId
        }
      } else {
        console.error(`❌ Failed to create library: ${createResult.message}`)
      }
    } catch (error) {
      console.error(`❌ Failed to get ${libraryName} library ID:`, error)
    }

    return null
  }

  async createGalleryImageLibrary(): Promise<{ success: boolean; message: string }> {
    try {
      const graphClient = await sharepointServiceProduction.getGraphClient()
      const siteId = await sharepointServiceProduction.getSiteIdForImages()

      if (!graphClient || !siteId) {
        return { success: false, message: 'SharePoint client not initialized' }
      }

      // Create Art Gallery Images library
      const library = await graphClient
        .api(`/sites/${siteId}/lists`)
        .post({
          displayName: this.galleryLibraryName,
          list: { template: 'documentLibrary' }
        })

      console.log('Created Art Gallery Images library:', library.id)

      // Get existing columns to avoid conflicts
      const existingColumns = await graphClient
        .api(`/sites/${siteId}/lists/${library.id}/columns`)
        .get()

      const existingColumnNames = new Set(
        existingColumns.value.map((col: any) => col.name.toLowerCase())
      )

      console.log('📋 Existing columns:', Array.from(existingColumnNames))

      // Helper function to add column only if it doesn't exist
      const addColumnIfNotExists = async (columnName: string, columnDef: any) => {
        if (existingColumnNames.has(columnName.toLowerCase())) {
          console.log(`⏭️ Column "${columnName}" already exists, skipping`)
          return
        }

        try {
          await graphClient
            .api(`/sites/${siteId}/lists/${library.id}/columns`)
            .post({ name: columnName, ...columnDef })
          console.log(`✅ Added column: ${columnName}`)
        } catch (error: any) {
          console.warn(`⚠️ Could not add column "${columnName}":`, error.message)
        }
      }

      // Add custom columns for gallery metadata (skip if they exist)
      await addColumnIfNotExists('Title', { text: {} })
      await addColumnIfNotExists('Description', { text: { allowMultipleLines: true } })
      await addColumnIfNotExists('Category', { text: {} })
      await addColumnIfNotExists('Tags', { text: { allowMultipleLines: true } })
      await addColumnIfNotExists('IsActive', { boolean: {} })
      await addColumnIfNotExists('GalleryItemId', { text: {} })

      return {
        success: true,
        message: 'Art Gallery Images library created successfully'
      }
    } catch (error: any) {
      console.error('Failed to create Art Gallery Images library:', error)
      return {
        success: false,
        message: `Failed to create library: ${error.message}`
      }
    }
  }

  async uploadImage(
    file: File,
    category: 'classes' | 'kits' | 'gallery',
    itemId?: string
  ): Promise<{ success: boolean; message: string; imageFile?: SharePointImageFile }> {
    try {
      const graphClient = await sharepointServiceProduction.getGraphClient()
      const siteId = await sharepointServiceProduction.getSiteIdForImages()
      const libraryId = await this.getImageLibraryId(category)

      if (!graphClient || !siteId || !libraryId) {
        return { success: false, message: 'SharePoint connection not available' }
      }

      // Convert HEIC/HEIF to JPEG before uploading
      let fileToUpload = file
      let fileExtension = file.name.split('.').pop()?.toLowerCase() || ''
      const isHEIC = fileExtension === 'heic' || fileExtension === 'heif'

      if (isHEIC) {
        console.log(`🔄 Converting HEIC image to JPEG: ${file.name}`)
        try {
          // Dynamically import heic2any
          const heic2any = (await import('heic2any')).default

          // Convert HEIC to JPEG
          const convertedBlob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.9 // High quality JPEG
          })

          // Handle array of blobs (heic2any can return multiple images)
          const blobToUse = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob

          // Create a new File object from the converted blob
          const originalName = file.name.replace(/\.(heic|heif)$/i, '')
          fileToUpload = new File([blobToUse], `${originalName}.jpg`, { type: 'image/jpeg' })
          fileExtension = 'jpg'

          console.log(`✅ Converted ${file.name} to JPEG (${(blobToUse.size / 1024).toFixed(2)} KB)`)
        } catch (conversionError: any) {
          console.error('❌ HEIC conversion failed:', conversionError)
          return {
            success: false,
            message: `Failed to convert HEIC image: ${conversionError.message}`
          }
        }
      }

      // Create unique filename
      const timestamp = Date.now()
      const baseFileName = fileToUpload.name.split('.').slice(0, -1).join('.')
      const sanitizedName = baseFileName.replace(/[^a-zA-Z0-9-_]/g, '_')
      const fileName = `${category}_${sanitizedName}_${timestamp}.${fileExtension}`

      // Upload file to document library
      const uploadResponse = await graphClient
        .api(`/sites/${siteId}/lists/${libraryId}/drive/root:/${fileName}:/content`)
        .put(fileToUpload)

      console.log('Upload response structure:', JSON.stringify(uploadResponse, null, 2))

      // Get the drive item ID from upload response
      const driveItemId = uploadResponse.id
      if (!driveItemId) {
        console.error('No drive item ID found in upload response')
        throw new Error('Unable to determine drive item ID from upload response')
      }

      // Get file information including the SharePoint list item details
      const fileInfo = await graphClient
        .api(`/sites/${siteId}/lists/${libraryId}/drive/items/${driveItemId}`)
        .expand('listItem')
        .get()

      console.log('File info with listItem:', JSON.stringify(fileInfo, null, 2))

      // Ensure all gallery metadata columns exist before setting metadata
      if (category === 'gallery') {
        await this.ensureGalleryMetadataColumns(graphClient, siteId, libraryId)
      }

      // Update metadata fields on the list item if it exists
      if (fileInfo.listItem && fileInfo.listItem.id) {
        try {
          console.log(`📝 Setting metadata on list item ${fileInfo.listItem.id}`)

          await graphClient
            .api(`/sites/${siteId}/lists/${libraryId}/items/${fileInfo.listItem.id}/fields`)
            .patch({
              Category: category,
              IsActive: true
            })

          console.log('✅ Metadata set successfully')
        } catch (metadataError: any) {
          console.warn('⚠️ Could not set metadata (columns may not exist yet):', metadataError.message)
        }
      }

      let thumbnailUrl: string | undefined
      try {
        const thumbnails = await graphClient
          .api(`/sites/${siteId}/lists/${libraryId}/drive/items/${driveItemId}/thumbnails`)
          .get()

        if (thumbnails.value && thumbnails.value.length > 0 && thumbnails.value[0].medium) {
          thumbnailUrl = thumbnails.value[0].medium.url
        }
      } catch (thumbnailError) {
        console.log('Could not generate thumbnail:', thumbnailError)
      }

      const sharePointImageFile: SharePointImageFile = {
        id: uploadResponse.id,
        name: fileName,
        webUrl: fileInfo.webUrl,
        downloadUrl: fileInfo['@microsoft.graph.downloadUrl'],
        category,
        size: fileInfo.size,
        created: fileInfo.createdDateTime,
        modified: fileInfo.lastModifiedDateTime,
        libraryId, // Include library ID for proxy function
        thumbnailUrl,
        itemId,
        eTag: fileInfo.eTag,
        lastModified: fileInfo.lastModifiedDateTime,
        isActive: true // Default to active
      }

      return {
        success: true,
        message: `Image uploaded successfully as ${fileName}`,
        imageFile: sharePointImageFile
      }
    } catch (error: any) {
      console.error('Failed to upload image:', error)
      return {
        success: false,
        message: `Failed to upload image: ${error.message}`
      }
    }
  }

  async getImages(category?: 'classes' | 'kits' | 'gallery'): Promise<SharePointImageFile[]> {
    try {
      const graphClient = await sharepointServiceProduction.getGraphClient()
      const siteId = await sharepointServiceProduction.getSiteIdForImages()
      const libraryId = await this.getImageLibraryId(category)

      if (!graphClient || !siteId || !libraryId) {
        console.warn('SharePoint connection not available')
        return []
      }

      // For gallery, we use a dedicated library (SaltyMermaid_Gallery) so all items are gallery items
      // For other categories, items share the Website Images library and need filtering
      // However, Category field might not exist yet, so we get all items and filter client-side
      let query = `/sites/${siteId}/lists/${libraryId}/items?expand=fields,driveItem`

      console.log(`📥 Fetching images from library ${libraryId} (category: ${category || 'all'})`)

      const response = await graphClient.api(query).get()
      console.log(`📦 Received ${response.value?.length || 0} items from SharePoint`)

      const images: SharePointImageFile[] = await Promise.all(
        response.value.map(async (item: any) => {
          let thumbnailUrl: string | undefined
          try {
            if (item.driveItem) {
              const thumbnails = await graphClient
                .api(`/sites/${siteId}/lists/${libraryId}/drive/items/${item.driveItem.id}/thumbnails`)
                .get()

              if (thumbnails.value && thumbnails.value.length > 0 && thumbnails.value[0].medium) {
                thumbnailUrl = thumbnails.value[0].medium.url
              }
            }
          } catch (thumbnailError) {
            console.log('Could not get thumbnail for item:', item.id)
          }

          return {
            id: item.driveItem?.id || item.id,
            name: item.driveItem?.name || item.fields.FileLeafRef,
            webUrl: item.driveItem?.webUrl || '',
            downloadUrl: item.driveItem?.['@microsoft.graph.downloadUrl'] || '',
            category: item.fields.Category || 'uncategorized',
            size: item.driveItem?.size || 0,
            created: item.fields.Created,
            modified: item.fields.Modified,
            libraryId, // Include library ID for proxy function
            thumbnailUrl,
            itemId: item.fields.ItemId,
            // Gallery-specific fields
            title: item.fields.Title,
            description: item.fields.ArtworkDescription,
            tags: item.fields.Tags ? item.fields.Tags.split(',').map((t: string) => t.trim()) : [],
            isActive: item.fields.IsActive !== false,
            eTag: item.driveItem?.eTag,
            lastModified: item.fields.Modified
          }
        })
      )

      // Client-side filtering by category if needed
      // For gallery category, all items in SaltyMermaid_Gallery are gallery items
      // For other categories using Website Images library, filter by Category field if it exists
      const filteredImages = category && category !== 'gallery'
        ? images.filter(img => img.category === category)
        : images

      console.log(`✅ Returning ${filteredImages.length} images (after filtering for category: ${category || 'all'})`)

      return filteredImages
    } catch (error) {
      console.error('Failed to get images:', error)
      return []
    }
  }

  // Download and cache image locally for website use
  async downloadImageForLocalUse(imageFile: SharePointImageFile): Promise<{ success: boolean; message: string; localDataUrl?: string }> {
    try {
      if (!imageFile.downloadUrl) {
        return { success: false, message: 'No download URL available' }
      }

      // Check if already cached
      const cachedImages = this.getLocalImageCache()
      const existing = cachedImages.find(img => img.id === imageFile.id)
      if (existing && existing.localDataUrl) {
        return { 
          success: true, 
          message: 'Image already cached locally', 
          localDataUrl: existing.localDataUrl 
        }
      }

      // Download the image
      const response = await fetch(imageFile.downloadUrl)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const blob = await response.blob()
      const dataUrl = await this.blobToDataUrl(blob)

      // Cache the image locally
      const cachedImage = {
        ...imageFile,
        localDataUrl: dataUrl,
        isSelected: true
      }

      this.cacheImageLocally(cachedImage)

      return { 
        success: true, 
        message: 'Image downloaded and cached successfully', 
        localDataUrl: dataUrl 
      }
    } catch (error: any) {
      console.error('Failed to download image:', error)
      return { 
        success: false, 
        message: `Failed to download image: ${error.message}` 
      }
    }
  }

  // Upload local file to both website and SharePoint
  async uploadFromLocalFile(
    file: File, 
    category: 'classes' | 'kits' | 'gallery', 
    itemId?: string
  ): Promise<{ success: boolean; message: string; sharePointFile?: SharePointImageFile; localDataUrl?: string }> {
    try {
      // First upload to SharePoint
      const sharePointResult = await this.uploadImage(file, category, itemId)
      if (!sharePointResult.success || !sharePointResult.imageFile) {
        return sharePointResult
      }

      // Convert file to data URL for local use
      const localDataUrl = await this.fileToDataUrl(file)

      // Cache locally with SharePoint metadata
      const cachedImage: SharePointImageFile = {
        ...sharePointResult.imageFile,
        localDataUrl,
        isSelected: true
      }

      this.cacheImageLocally(cachedImage)

      return {
        success: true,
        message: 'Image uploaded to SharePoint and cached locally',
        sharePointFile: sharePointResult.imageFile,
        localDataUrl
      }
    } catch (error: any) {
      console.error('Failed to upload from local file:', error)
      return {
        success: false,
        message: `Failed to upload: ${error.message}`
      }
    }
  }

  // Get all images from SharePoint with local cache info
  async getImagesWithLocalInfo(): Promise<SharePointImageFile[]> {
    const sharePointImages = await this.getImages()
    const localCache = this.getLocalImageCache()

    return sharePointImages.map(spImage => {
      const cached = localCache.find(local => local.id === spImage.id)
      return {
        ...spImage,
        localDataUrl: cached?.localDataUrl,
        isSelected: cached?.isSelected || false
      }
    })
  }

  // Local cache management
  private getLocalImageCache(): SharePointImageFile[] {
    try {
      const cached = localStorage.getItem('sharepoint_image_cache')
      return cached ? JSON.parse(cached) : []
    } catch (error) {
      console.error('Failed to read image cache:', error)
      return []
    }
  }

  private cacheImageLocally(image: SharePointImageFile): void {
    try {
      const cache = this.getLocalImageCache()
      const existingIndex = cache.findIndex(img => img.id === image.id)

      if (existingIndex >= 0) {
        cache[existingIndex] = image
      } else {
        cache.push(image)
      }

      localStorage.setItem('sharepoint_image_cache', JSON.stringify(cache))
      
      // Notify subscribers that the image cache has changed
      this.notifySubscribers()
    } catch (error) {
      console.error('Failed to cache image locally:', error)
    }
  }

  // Utility methods
  private async fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  private async blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  // Mark/unmark images for website use
  async toggleImageSelection(imageId: string, selected: boolean): Promise<void> {
    const cache = this.getLocalImageCache()
    const imageIndex = cache.findIndex(img => img.id === imageId)

    if (imageIndex >= 0) {
      cache[imageIndex].isSelected = selected
      localStorage.setItem('sharepoint_image_cache', JSON.stringify(cache))
      
      // Notify subscribers that the image cache has changed
      this.notifySubscribers()
    }
  }

  // Get images selected for website use
  getSelectedImagesForWebsite(): SharePointImageFile[] {
    return this.getLocalImageCache().filter(img => img.isSelected && img.localDataUrl)
  }

  // Sync selected images from SharePoint to local cache
  async syncSelectedImagesFromSharePoint(): Promise<{ success: boolean; message: string; syncedCount: number }> {
    try {
      const localCache = this.getLocalImageCache()
      const selectedImages = localCache.filter(img => img.isSelected)
      let syncedCount = 0

      for (const localImage of selectedImages) {
        // Get latest version from SharePoint
        const sharePointImages = await this.getImages()
        const sharePointImage = sharePointImages.find(sp => sp.id === localImage.id)

        if (sharePointImage) {
          // Check if SharePoint version is newer
          const sharePointModified = new Date(sharePointImage.modified)
          const localModified = new Date(localImage.modified)

          if (sharePointModified > localModified) {
            // Download updated version
            const downloadResult = await this.downloadImageForLocalUse(sharePointImage)
            if (downloadResult.success) {
              syncedCount++
            }
          }
        } else {
          // Image was deleted from SharePoint, remove from local cache
          this.removeFromLocalCache(localImage.id)
          syncedCount++
        }
      }

      return {
        success: true,
        message: `Sync completed. ${syncedCount} images updated.`,
        syncedCount
      }
    } catch (error: any) {
      console.error('Failed to sync from SharePoint:', error)
      return {
        success: false,
        message: `Sync failed: ${error.message}`,
        syncedCount: 0
      }
    }
  }

  private removeFromLocalCache(imageId: string): void {
    try {
      const cache = this.getLocalImageCache()
      const filtered = cache.filter(img => img.id !== imageId)
      localStorage.setItem('sharepoint_image_cache', JSON.stringify(filtered))
      
      // Notify subscribers that the image cache has changed
      this.notifySubscribers()
    } catch (error) {
      console.error('Failed to remove from cache:', error)
    }
  }

  // Update image category in SharePoint
  async updateImageMetadata(
    imageId: string,
    metadata: {
      Title?: string
      ArtworkDescription?: string
      Tags?: string
      IsActive?: boolean
      Category?: string
    }
  ): Promise<{ success: boolean; message: string }> {
    try {
      const graphClient = await sharepointServiceProduction.getGraphClient()
      const siteId = await sharepointServiceProduction.getSiteIdForImages()

      // Determine which library to use based on current image category
      // For gallery images, we need to get the gallery library ID
      const libraryId = await this.getImageLibraryId('gallery')

      if (!graphClient || !siteId || !libraryId) {
        return { success: false, message: 'SharePoint connection not available' }
      }

      console.log(`📝 Updating metadata for image ${imageId} in library ${libraryId}`)

      // Ensure all gallery metadata columns exist before updating
      await this.ensureGalleryMetadataColumns(graphClient, siteId, libraryId)

      // Get all items and find the one with matching drive item ID
      const items = await graphClient
        .api(`/sites/${siteId}/lists/${libraryId}/items`)
        .expand('fields,driveItem')
        .get()

      const targetItem = items.value.find((item: any) =>
        item.driveItem && item.driveItem.id === imageId
      )

      if (!targetItem) {
        return { success: false, message: 'Image not found in SharePoint' }
      }

      console.log(`✅ Found list item ${targetItem.id} for drive item ${imageId}`)

      // Update the metadata fields
      await graphClient
        .api(`/sites/${siteId}/lists/${libraryId}/items/${targetItem.id}/fields`)
        .patch(metadata)

      console.log(`✅ Metadata updated successfully`)

      return { success: true, message: 'Image metadata updated successfully' }
    } catch (error: any) {
      console.error('Failed to update image metadata:', error)
      return { success: false, message: `Failed to update metadata: ${error.message}` }
    }
  }

  async deleteImage(imageId: string, category?: 'classes' | 'kits' | 'gallery'): Promise<{ success: boolean; message: string }> {
    try {
      const graphClient = await sharepointServiceProduction.getGraphClient()
      const siteId = await sharepointServiceProduction.getSiteIdForImages()
      const libraryId = await this.getImageLibraryId(category || 'gallery')

      if (!graphClient || !siteId || !libraryId) {
        return { success: false, message: 'SharePoint connection not available' }
      }

      console.log(`🗑️ Deleting image ${imageId} from library ${libraryId}`)

      // Delete the drive item directly
      await graphClient
        .api(`/sites/${siteId}/lists/${libraryId}/drive/items/${imageId}`)
        .delete()

      console.log(`✅ Image deleted successfully from SharePoint`)

      // Remove from local cache
      const cache = this.getLocalImageCache()
      const filteredCache = cache.filter(img => img.id !== imageId)
      localStorage.setItem('sharepoint_image_cache', JSON.stringify(filteredCache))
      this.notifySubscribers()

      return { success: true, message: 'Image deleted successfully' }
    } catch (error: any) {
      console.error('Failed to delete image:', error)
      return { success: false, message: `Failed to delete image: ${error.message}` }
    }
  }

  async updateImageCategory(imageId: string, newCategory: 'classes' | 'kits' | 'gallery'): Promise<{ success: boolean; message: string }> {
    try {
      const graphClient = await sharepointServiceProduction.getGraphClient()
      const siteId = await sharepointServiceProduction.getSiteIdForImages()
      const libraryId = await this.getImageLibraryId()

      if (!graphClient || !siteId || !libraryId) {
        return { success: false, message: 'SharePoint connection not available' }
      }

      // First check if Category column exists, if not create it
      await this.ensureCategoryColumn(graphClient, siteId, libraryId)

      // Get all items and find the one with matching drive item ID
      const items = await graphClient
        .api(`/sites/${siteId}/lists/${libraryId}/items`)
        .expand('fields,driveItem')
        .get()

      const targetItem = items.value.find((item: any) =>
        item.driveItem && item.driveItem.id === imageId
      )

      if (!targetItem) {
        return { success: false, message: 'Image not found in SharePoint' }
      }

      // Update the category
      await graphClient
        .api(`/sites/${siteId}/lists/${libraryId}/items/${targetItem.id}/fields`)
        .patch({
          Category: newCategory
        })

      // Update local cache
      const cache = this.getLocalImageCache()
      const imageIndex = cache.findIndex(img => img.id === imageId)
      if (imageIndex >= 0) {
        cache[imageIndex].category = newCategory
        localStorage.setItem('sharepoint_image_cache', JSON.stringify(cache))

        // Notify subscribers that the image cache has changed
        this.notifySubscribers()
      }

      // IMPORTANT: Also write updated images to JSON file so public site sees the changes
      try {
        console.log('SharePoint: Writing updated images to JSON file after category change...')
        const selectedImages = this.getSelectedImagesForWebsite()
        const response = await fetch('/api/write-json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ file: 'images.json', data: selectedImages })
        })
        if (response.ok) {
          console.log('SharePoint: Successfully wrote to images.json after category change')
        } else {
          console.error('SharePoint: Failed to write to images.json after category change')
        }
      } catch (jsonError) {
        console.error('SharePoint: Error writing to images.json:', jsonError)
      }

      return { success: true, message: `Image category updated to ${newCategory}` }
    } catch (error: any) {
      console.error('Failed to update image category:', error)
      return { success: false, message: `Failed to update category: ${error.message}` }
    }
  }

  // Ensure gallery metadata columns exist in the document library
  private async ensureGalleryMetadataColumns(graphClient: any, siteId: string, libraryId: string): Promise<void> {
    try {
      // Get all existing columns
      const columns = await graphClient
        .api(`/sites/${siteId}/lists/${libraryId}/columns`)
        .get()

      const existingColumns = new Set(
        columns.value.map((col: any) => col.name.toLowerCase())
      )

      console.log('📋 Existing columns:', Array.from(existingColumns))

      // Helper function to add column if it doesn't exist
      const addColumnIfNotExists = async (columnDef: any, columnName: string) => {
        if (!existingColumns.has(columnName.toLowerCase())) {
          console.log(`Creating ${columnName} column in gallery library...`)
          try {
            await graphClient
              .api(`/sites/${siteId}/lists/${libraryId}/columns`)
              .post(columnDef)
            console.log(`✅ ${columnName} column created successfully`)
          } catch (error: any) {
            if (error.statusCode === 409) {
              console.log(`⚠️ ${columnName} column already exists (409 conflict)`)
            } else {
              throw error
            }
          }
        } else {
          console.log(`✅ ${columnName} column already exists`)
        }
      }

      // ArtworkDescription column (multi-line text) - using custom name to avoid conflicts with built-in Description
      await addColumnIfNotExists({
        name: 'ArtworkDescription',
        displayName: 'Artwork Description',
        enforceUniqueValues: false,
        hidden: false,
        indexed: false,
        required: false,
        text: {
          allowMultipleLines: true,
          maxLength: 1000
        }
      }, 'ArtworkDescription')

      // Tags column (single line text)
      await addColumnIfNotExists({
        name: 'Tags',
        displayName: 'Tags',
        enforceUniqueValues: false,
        hidden: false,
        indexed: false,
        required: false,
        text: {
          allowMultipleLines: false,
          maxLength: 255
        }
      }, 'Tags')

      // IsActive column (boolean/yes-no)
      await addColumnIfNotExists({
        name: 'IsActive',
        displayName: 'IsActive',
        enforceUniqueValues: false,
        hidden: false,
        indexed: false,
        required: false,
        boolean: {}
      }, 'IsActive')

      // Category column (choice) - include all art gallery categories
      // Check if Category column exists and needs updating
      if (existingColumns.has('category')) {
        console.log('Category column exists, checking if it needs updating...')
        try {
          // Get the current column definition
          const categoryColumn = columns.value.find((col: any) =>
            col.name.toLowerCase() === 'category'
          )

          // Update the column to include all art categories
          await graphClient
            .api(`/sites/${siteId}/lists/${libraryId}/columns/${categoryColumn.id}`)
            .patch({
              choice: {
                allowTextEntry: true,  // Allow custom text entry
                displayAs: 'dropDownMenu',
                choices: [
                  'gallery',
                  'Paintings',
                  'Sculptures',
                  'Photography',
                  'Mixed Media',
                  'Digital Art',
                  'Watercolor',
                  'Acrylic',
                  'Oil',
                  'Other'
                ]
              }
            })
          console.log('✅ Category column updated with art categories')
        } catch (updateError: any) {
          console.warn('⚠️ Could not update Category column:', updateError.message)
        }
      } else {
        // Create the column if it doesn't exist
        await addColumnIfNotExists({
          name: 'Category',
          displayName: 'Category',
          enforceUniqueValues: false,
          hidden: false,
          indexed: false,
          required: false,
          choice: {
            allowTextEntry: true,  // Allow custom text entry
            displayAs: 'dropDownMenu',
            choices: [
              'gallery',
              'Paintings',
              'Sculptures',
              'Photography',
              'Mixed Media',
              'Digital Art',
              'Watercolor',
              'Acrylic',
              'Oil',
              'Other'
            ]
          }
        }, 'Category')
      }

      console.log('✅ All gallery metadata columns ensured')
    } catch (error) {
      console.error('Error ensuring gallery metadata columns:', error)
      // Don't throw here, let the main update proceed and fail gracefully if needed
    }
  }

  // Ensure Category column exists in the document library
  private async ensureCategoryColumn(graphClient: any, siteId: string, libraryId: string): Promise<void> {
    try {
      // Check if Category column exists
      const columns = await graphClient
        .api(`/sites/${siteId}/lists/${libraryId}/columns`)
        .get()

      const categoryExists = columns.value.some((col: any) =>
        col.name === 'Category' || col.displayName === 'Category'
      )

      if (!categoryExists) {
        console.log('Creating Category column in Website Images library')
        // Create the Category column
        const categoryColumn = {
          name: 'Category',
          displayName: 'Category',
          enforceUniqueValues: false,
          hidden: false,
          indexed: false,
          required: false,
          choice: {
            allowTextEntry: false,
            displayAs: 'dropDownMenu',
            choices: ['classes', 'kits', 'gallery']
          }
        }

        await graphClient
          .api(`/sites/${siteId}/lists/${libraryId}/columns`)
          .post(categoryColumn)

        console.log('Category column created successfully')
      }
    } catch (error) {
      console.error('Error ensuring Category column exists:', error)
      // Don't throw here, let the main update proceed and fail gracefully if needed
    }
  }

  // Subscribe to changes
  subscribe(callback: () => void): () => void {
    this.subscribers.push(callback)
    return () => {
      const index = this.subscribers.indexOf(callback)
      if (index > -1) {
        this.subscribers.splice(index, 1)
      }
    }
  }

  // Notify all subscribers of changes
  private notifySubscribers(): void {
    this.subscribers.forEach(callback => {
      try {
        callback()
      } catch (error) {
        console.error('Error in SharePoint image service subscriber:', error)
      }
    })
  }
}

export const sharepointImageService = new SharePointImageService()